/** @format */

import {useEffect, useMemo, useState, useRef, useCallback} from 'react';

import {useParams} from 'react-router-dom';

import {LinearProgress} from '@mui/material';

import {ScreenLayout} from 'layouts/ScreenLayout';

import GoalFactAiChart from 'components/charts/GoalFactAiChart';

import {toStr} from 'helpers/stringHelpers';
import {useMixpanelContext} from 'contexts/MixpanelContext';

import FullAiReportBreadcrumbs from './components/FullAiReportBreadcrumbs';
import MainInfo from './components/MainInfo';

import ExpandableTables from './components/ExpandableTables';
import SegmentTable from './components/SegmentTable';
import ForecastChart from './components/ForecastChart';

import {useFullReportData} from './hooks/useFullReportData';

import {ChartsContainer, PaddingContainer, SegmentsContainer} from './styles';
import type {EntityType} from './constants';
import {useGetCurrentUserCompanyNameQuery} from './graphql/__generated__';

const FullReport = () => {
  const {companyId, orderId} = useParams<{
    companyId: string;
    orderId: string;
  }>();
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>('');
  const segmentTableRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const {mixpanel} = useMixpanelContext();

  const hasSentViewEvent = useRef(false);

  const {data: companyData} = useGetCurrentUserCompanyNameQuery({
    variables: {companyId: String(companyId)},
  });

  const companyName = companyData?.currentUser?.company?.name || '';
  const companyUuid = companyData?.currentUser?.company?.uuid;

  const {
    aiReportDataLoading,
    tableData,
    reportInfo,
    chartData,
    budgetChartData,
    campaigns,
    metricGoals,
  } = useFullReportData({
    orderId,
    companyId,
  });

  const {
    kpiName,
    kpiUnits,
    targetKpi,
    targetBudget,
    plannedBudget,
    actualKpi,
    actualBudget,
    expectedKpi,
    expectedBudget,
    expertResume,
    attributionModel,
    dateFrom,
    dateTo,
    buildDate,
  } = reportInfo;

  useEffect(() => {
    if (!aiReportDataLoading && companyUuid && !hasSentViewEvent.current) {
      hasSentViewEvent.current = true;
      mixpanel.track('ai_report_view_results', {
        order_id: orderId,
        marvin_id: orderId,
        project_uuid: companyUuid,
      });
    }
  }, [aiReportDataLoading, companyUuid, orderId, mixpanel]);

  const filteredTableData = useMemo(() => {
    if (!selectedCampaignId) {
      return tableData;
    }

    return tableData
      .map(table => ({
        ...table,
        data: table.data.filter(segment => segment.campaignId === selectedCampaignId),
      }))
      .filter(table => table.data.length > 0);
  }, [tableData, selectedCampaignId]);

  const handleCampaignFilterChange = (campaignId: string) => {
    setSelectedCampaignId(campaignId);
  };

  const scrollToSegment = useCallback((segmentId: string) => {
    const element = segmentTableRefs.current[segmentId];
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, []);

  const handleOpenSubRows = useCallback(
    (isOpen: boolean, campaignId?: string) => {
      if (isOpen) {
        mixpanel.track('ai_report_click_row_details', {
          project_uuid: companyUuid,
          marvin_id: orderId,
          order_id: orderId,
          campaign_id: campaignId,
        });
      }
    },
    [mixpanel, companyUuid, orderId],
  );

  const handleCampaignNameClick = useCallback(
    (campaignId: string) => {
      mixpanel.track('ai_report_click_campaign_name', {
        project_uuid: companyUuid,
        marvin_id: orderId,
        order_id: orderId,
        campaign_id: campaignId,
      });
    },
    [mixpanel, companyUuid, orderId],
  );

  if (aiReportDataLoading) {
    return <LinearProgress style={{flex: 1}} />;
  }

  return (
    <ScreenLayout>
      <PaddingContainer data-testid='full-report-main-content'>
        <FullAiReportBreadcrumbs companyName={companyName} reportDate={buildDate} />
        <MainInfo
          startDate={dateFrom || ''}
          endDate={dateTo || ''}
          attributionModel={attributionModel}
          buildDate={buildDate}
          summaryInfo={toStr(expertResume)}
        />
        <ChartsContainer data-testid='full-report-charts'>
          <GoalFactAiChart
            targetBudget={targetBudget}
            targetKpi={targetKpi}
            actualBudget={actualBudget}
            actualKpi={actualKpi}
            expectedBudget={expectedBudget}
            expectedKpi={expectedKpi}
            kpiName={kpiName}
            kpiUnits={kpiUnits}
          />
          <ForecastChart
            data={chartData}
            kpiName={kpiName}
            targetValue={targetKpi}
            currentValue={actualKpi}
            forecastValue={expectedKpi}
            onSegmentClick={scrollToSegment}
          />
          <ForecastChart
            isBudgetChart
            data={budgetChartData}
            kpiName={kpiName}
            targetValue={targetBudget}
            currentValue={actualBudget}
            forecastValue={expectedBudget}
            onSegmentClick={scrollToSegment}
          />
        </ChartsContainer>
        <ExpandableTables
          campaigns={campaigns}
          metricGoals={metricGoals}
          onCampaignFilterChange={handleCampaignFilterChange}
          selectedCampaignId={selectedCampaignId}
        />
      </PaddingContainer>
      <SegmentsContainer data-testid='full-report-segments'>
        {filteredTableData.map(table => {
          const {groupKey, data, maxKpiFact, maxForecastKpi, maxForecastBudget} = table;

          return (
            <SegmentTable
              key={groupKey}
              ref={el => {
                segmentTableRefs.current[groupKey] = el;
              }}
              entityType={groupKey as EntityType}
              segments={data}
              maxKpiFact={maxKpiFact}
              maxForecastKpi={maxForecastKpi}
              maxForecastBudget={maxForecastBudget}
              kpiName={kpiName}
              kpiUnits={kpiUnits}
              targetKpi={targetKpi}
              targetBudget={targetBudget}
              plannedBudget={plannedBudget}
              onCampaignFilterClick={handleCampaignFilterChange}
              onOpenSubRows={handleOpenSubRows}
              onCampaignNameClick={handleCampaignNameClick}
            />
          );
        })}
      </SegmentsContainer>
    </ScreenLayout>
  );
};

export default FullReport;
